'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { HarlequinBadge } from '../../../components/HarlequinBadge';
import { type SimilarStory } from '../../../services/api/goApi';
import { getStoryPath } from '../../../utils/slugUtils';
import { getStoryAuthor } from '../../../utils/storyAuthor';

export const SimilarBooks: React.FC<{
  similarStories: SimilarStory[];
  locale: string;
}> = ({ similarStories, locale }) => {
  const { t } = useTranslation();

  if (!similarStories || similarStories.length === 0) {
    return null;
  }

  return (
    <div className="pt-12">
      <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
        {t('summary:recommendations')}
      </h2>
      <div className="flex overflow-x-auto space-x-4 -mx-4 px-4 py-4 no-scrollbar">
        {similarStories.map((story) => {
          const storyId = story.original_story_id || (typeof story.id === 'number' ? story.id : parseInt(story.id, 10));
          const author = getStoryAuthor(story);
          const storyPath = getStoryPath(story.name, storyId, locale, story.url_slug);

          return (
            <Link key={story.id} href={`/${locale}${storyPath}`} className="shrink-0 w-36 group ">
              <div className="relative aspect-[194/300] w-full rounded-[16px] overflow-hidden shadow-lg">
                {story.cover && (
                  <Image src={story.cover} alt={story.name} fill sizes="144px" className="object-cover" />
                )}
                <HarlequinBadge publisher={story.publisher} />
              </div>
              <h3 className="mt-2 text-sm font-bold text-neutral-900 truncate">{story.name}</h3>
              {author && (
                <p className="text-xs text-neutral-500 truncate">
                  {t('common:by_author', { author, interpolation: { escapeValue: false } })}
                </p>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
};
